import { useCallback, useMemo, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import moment from 'moment/moment'

import { useSites } from 'store'
import { useSort } from './store'


const OPERATION_CDRS = gql`
  query VoipCDRs($siteSfId: ID!, $startAt: String!, $endAt: String!) {
    cdrs: voipCDRs(site_sf_id: $siteSfId, start_at: $startAt, end_at: $endAt) {
      type
      status
      calling_number
      called_number
      start_date
      answer_date
      end_date
      is_internal
      duration_in_second
      waiting_duration_in_second
      owner_name
      recipient
      transferred_to
    }
  }
`

const PAGE_SIZE = 20

const durationToString = (value) => {
  let duration
  if (value === null) {
    duration = '0:00'
  } else {
    const minutes = moment().startOf('day').add(value, 'seconds').format('m')
    const seconds = moment().startOf('day').add(value, 'seconds').format('ss')
    if (value > 60) {
      duration = `${minutes}:${seconds}`
    } else {
      duration = `0:${seconds}`
    }
  }
  return duration
}
const useCDRs = ({filters}) => {
  const {t} = useTranslation()
  const [page, setPage] = useState(0)
  const [{selected}] = useSites()
  const [sort] = useSort()
  const { data, loading, error } = useQuery(OPERATION_CDRS, {
    variables: {
      siteSfId: selected[0].sf_id,
      startAt: filters.startAt.format('yyyy-MM-DD 00:00:00'),
      endAt: filters.endAt.format('yyyy-MM-DD 23:59:59'),
    }, fetchPolicy: 'network-only'
  })

  const statuses = useMemo(() => {
    return data?.cdrs?.reduce((acc, cdr) => ({
      answered: cdr.status === 'answered' ? acc.answered + 1 : acc.answered,
      unanswered: cdr.status === 'unanswered' ? acc.unanswered + 1 : acc.unanswered
    }), {answered: 0, unanswered: 0})
  }, [data])

  const types = useMemo(() => {
    return data?.cdrs?.reduce((acc, sip) => ({
      in: sip.type === 'in' ? acc.in + 1 : acc.in,
      out: sip.type === 'out' ? acc.out + 1 : acc.out,
      internal: sip.type === 'internal' ? acc.internal + 1 : acc.internal,
    }), {in: 0, out: 0, internal: 0})
  }, [data])

  const cdrs = useMemo(() => {
    return (data?.cdrs || [])
      .filter(cdr => {
        if (filters.statuses.length === 1) {
          return cdr.status === filters.statuses[0]
        }
        return cdr
      })
      .filter(cdr => {
        if ((filters.types.length === 1)) {
          return cdr.type === filters.types[0]
        }
        return cdr
      })
      .filter(cdr => {
        if (!filters.verbatim.length) {
          return cdr
        }
        return (cdr.called_number || '').toLowerCase().indexOf(filters.verbatim.toLowerCase()) > -1
          || (cdr.calling_number || '').toLowerCase().indexOf(filters.verbatim.toLowerCase()) > -1
      })
      .map(cdr => {
        return {
          ...cdr,
          duration: durationToString(cdr.duration_in_second),
          waiting_duration: durationToString(cdr.waiting_duration_in_second),
        }
      })
  }, [data, filters])
  const selection = useMemo(() => {
    return [...cdrs]
      .sort((a, b) => {
      if (['duration_in_second'].includes(sort.by)) {
        return sort.direction === 'asc'
          ? (a[sort.by] || 0) - (b[sort.by] || 0)
          : (b[sort.by] || 0) - (a[sort.by] || 0)
      }
      return sort.direction === 'asc'
        ? (a[sort.by] || '').localeCompare(b[sort.by] || '')
        : (b[sort.by] || '').localeCompare(a[sort.by] || '')
    })
  }, [cdrs, page, sort])

  const handleExport = useCallback(() => {
    const header = [
      t('voip.cdr.status'),
      t('voip.cdr.calling_number'),
      t('voip.cdr.called_number'),
      t('voip.cdr.start_date'),
      t('voip.cdr.end_date'),
      t('voip.cdr.duration'),
      t('voip.cdr.recipient'),
      t('voip.cdr.transferred_to'),
      t('voip.cdr.owner_name')
    ].join(';')
    const body = selection.map(cdr => [
      cdr.type,
      cdr.calling_number,
      cdr.called_number,
      cdr.start_date
        ? t('voip.datetime', {
          date: new Date(cdr.start_date).toLocaleDateString(),
          time: new Date(cdr.start_date).toLocaleTimeString()
        })
        : '-',
      cdr.end_date
        ? t('voip.datetime', {
          date: new Date(cdr.end_date).toLocaleDateString(),
          time: new Date(cdr.end_date).toLocaleTimeString()
        })
        : '-',
      cdr.duration,
      cdr.recipient,
      cdr.transferred_to,
      cdr.owner_name
    ].join(';'))
    return `data:text/csv;charset=utf-8,${[header, ...body].join('\n')}`
  }, [selection, t])

  return {
    loading,
    error,
    statuses,
    types,
    cdrs: selection.slice(0, (page + 1) * PAGE_SIZE),
    fetchNextPage: () => setPage(prev => prev + 1),
    shouldFetchNextPage: (page + 1) * PAGE_SIZE < cdrs.length,
    onPageReset: () => setPage(0),
    onExport: handleExport
  }
}

export default useCDRs